import { useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { assist } from 'store/notification/assist'
import { isBlank, htmlToText, textToHtml } from 'components/RichTextEditor'
import * as snackbar from 'utils/snackbar'

export default function ComposeAssist({ body, channel, onApply }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const anchor = useRef(null)

  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)

  const writing = isBlank(body)
  const current = writing ? '' : htmlToText(body)
  const canRun = !busy && (!writing || prompt.trim().length > 0)

  const run = async () => {
    if (!canRun) return

    setBusy(true)
    setResult(null)

    try {
      const response = await dispatch(
        assist({
          action: writing ? 'draft' : 'polish',
          channel,
          prompt: prompt.trim(),
          text: current,
        }),
      )

      if (response?.error) {
        snackbar.error(response.error?.data?.message || t('Could not reach the writing assistant.'))
        return
      }

      const text = response.payload?.text?.trim()

      if (!text) {
        snackbar.warning(t('The assistant did not return anything to use.'))
        return
      }

      setResult(text)
    } catch (error) {
      snackbar.error(error?.data?.message || t('Could not reach the writing assistant.'))
    } finally {
      setBusy(false)
    }
  }

  const apply = () => {
    onApply(channel === 'email' ? textToHtml(result) : result)
    setResult(null)
    setPrompt('')
    setOpen(false)
  }

  return (
    <>
      <Button
        ref={anchor}
        size="small"
        variant="outlined"
        startIcon={<AutoFixHighIcon fontSize="small" />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {writing ? t('Help me write') : t('Refine')}
      </Button>

      <Popover
        open={open}
        anchorEl={anchor.current}
        onClose={() => setOpen(false)}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 440, maxWidth: '95vw', p: 2, borderRadius: 2 } } }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          {writing ? t('Describe your message') : t('How should it change?')}
        </Typography>

        <TextField
          fullWidth
          autoFocus
          multiline
          minRows={2}
          size="small"
          value={prompt}
          disabled={busy}
          onChange={event => setPrompt(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              run()
            }
          }}
          placeholder={
            writing
              ? t('e.g. early dismissal Friday at 1pm, buses run early')
              : t('e.g. make it warmer and shorter — or leave blank to just polish it')
          }
        />

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.5 }}>
          <Button size="small" onClick={() => setOpen(false)} disabled={busy}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={run}
            disabled={!canRun}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {writing ? t('Write') : t('Refine')}
          </Button>
        </Stack>

        {result && (
          <>
            <Divider sx={{ my: 2 }} />

            {!writing && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {t('Current')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-wrap', maxHeight: 96, overflow: 'auto', mt: 0.25 }}
                >
                  {current}
                </Typography>
              </Box>
            )}

            <Typography variant="caption" color="primary.main" fontWeight={700}>
              {writing ? t('Suggested message') : t('Suggested rewrite')}
            </Typography>
            <Box
              sx={theme => ({
                mt: 0.5,
                p: 1.25,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.06),
                maxHeight: 200,
                overflow: 'auto',
              })}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {result}
              </Typography>
            </Box>

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.5 }}>
              <Button size="small" onClick={() => setResult(null)}>
                {t('Discard')}
              </Button>
              <Button size="small" variant="contained" onClick={apply}>
                {writing ? t('Use this') : t('Replace')}
              </Button>
            </Stack>
          </>
        )}
      </Popover>
    </>
  )
}
